/** Largest binary message the contract allows on a data channel; larger ones are split. */
export const MAX_CHANNEL_MESSAGE_BYTES = 16 * 1024;

const SCREEN_UNAVAILABLE = 'screen-unavailable';
const NOT_PERMITTED = 'not-permitted';
const SCREEN_ORIENTATION = 'screen-orientation';

/** Text messages the terminal sends on a `vnc` channel: notices, never RFB. */
export interface ScreenChannelNotices {
  /** No app can show its screen right now; the terminal closes the channel. */
  screenUnavailable(): void;
  /** The terminal refused the channel: the account may not view the screen. */
  notPermitted(): void;
  /**
   * How the app draws its screen, before the first RFB byte and whenever the operator turns it:
   * `rotated` content is drawn turned 90° counter-clockwise in a framebuffer of unchanged size.
   */
  screenOrientation(rotated: boolean): void;
}

/** The fields of the terminal's text messages the page reads. */
interface ChannelMessage {
  type?: unknown;
  rotated?: unknown;
}

/**
 * The terminal's `vnc` data channel as noVNC's raw channel. The RFB stream travels in binary
 * messages; the terminal's text messages are not RFB, so they go to `notices` and never reach
 * noVNC. noVNC checks for each of these members before attaching.
 */
export class ScreenChannel {
  binaryType: BinaryType = 'arraybuffer';
  onopen: ((event: Event) => void) | null = null;
  onmessage: ((event: MessageEvent) => void) | null = null;
  onclose: ((event: Event) => void) | null = null;
  onerror: ((event: Event) => void) | null = null;

  constructor(
    private readonly channel: RTCDataChannel,
    private readonly notices: ScreenChannelNotices,
  ) {
    channel.binaryType = 'arraybuffer';
    channel.onopen = (event) => this.onopen?.(event);
    channel.onclose = (event) => this.onclose?.(event);
    channel.onerror = (event) => this.onerror?.(event);
    channel.onmessage = (event) => this.receive(event);
  }

  get protocol(): string {
    return this.channel.protocol;
  }

  get readyState(): RTCDataChannelState {
    return this.channel.readyState;
  }

  send(data: ArrayBuffer | ArrayBufferView): void {
    const bytes = asBytes(data);
    for (
      let offset = 0;
      offset < bytes.byteLength;
      offset += MAX_CHANNEL_MESSAGE_BYTES
    ) {
      this.channel.send(
        bytes.subarray(offset, offset + MAX_CHANNEL_MESSAGE_BYTES),
      );
    }
  }

  close(): void {
    this.channel.close();
  }

  private receive(event: MessageEvent): void {
    if (typeof event.data === 'string') {
      this.notice(event.data);
    } else {
      this.onmessage?.(event);
    }
  }

  private notice(text: string): void {
    const message = parseMessage(text);
    const type = message?.type;
    if (type === SCREEN_UNAVAILABLE) {
      this.notices.screenUnavailable();
    } else if (type === NOT_PERMITTED) {
      this.notices.notPermitted();
    } else if (
      type === SCREEN_ORIENTATION &&
      typeof message?.rotated === 'boolean'
    ) {
      this.notices.screenOrientation(message.rotated);
    }
  }
}

function asBytes(data: ArrayBuffer | ArrayBufferView): Uint8Array {
  if (data instanceof ArrayBuffer) {
    return new Uint8Array(data);
  }
  return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
}

/** A text message as a JSON object, or null for text that is not one. */
function parseMessage(text: string): ChannelMessage | null {
  try {
    const message: unknown = JSON.parse(text);
    if (typeof message === 'object' && message !== null) {
      return message as ChannelMessage;
    }
    return null;
  } catch {
    return null;
  }
}
